import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Skeleton, Typography, Table } from 'antd';
import StackedAreaChart from '../../components/StackedAreaChart';
import DashboardLineChart from '../../components/DashboardLineChart';
import AssistantsPieChart from '../../components/AssistantsPieChart';
import ModelLatencyChart from '../../components/ModelLatency';
import { TOKEN_CHART_SERIES, TOKEN_TIME_SERIES } from '../../utils/chartTimeSeries';
import { getAudienceSummary, getAudienceTimeseries } from '../../services/api/audienceAnalytics';
import { timeRangeToRagPeriod } from '../../utils/timeRange';
import { useDashboard } from './DashboardContext';
import DashboardFilterBar from './DashboardFilterBar';
import DashboardStatCard from './DashboardStatCard';
import { formatNumber } from './dashboardUtils';

const { Text } = Typography;

const AUDIENCE_SERIES = [
  { key: 'sessions', label: 'Sessions', color: '#3b82f6' },
  { key: 'unique_visitors', label: 'Unique visitors', color: '#10b981' },
];

export default function DashboardUsage() {
  const navigate = useNavigate();

  const {
    keycloak,
    showAll,
    isAdmin,
    timeRange,
    loading,
    requestsChart,
    tokenChart,
    assistantUsageData,
    modelLatencyData,
    pieChartEmptyState,
    latencyChartEmptyState,
  } = useDashboard();

  const platformFlag = showAll && isAdmin;
  const period = timeRangeToRagPeriod(timeRange);

  const [audienceLoading, setAudienceLoading] = useState(true);
  const [audienceSummary, setAudienceSummary] = useState(null);
  const [audienceTimeseries, setAudienceTimeseries] = useState([]);

  const audienceOpts = useMemo(() => ({
    period,
    timeRange,
    showAll: platformFlag,
  }), [period, timeRange, platformFlag]);

  const loadAudience = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    setAudienceLoading(true);
    try {
      const [summary, ts] = await Promise.all([
        getAudienceSummary(keycloak, audienceOpts),
        getAudienceTimeseries(keycloak, audienceOpts),
      ]);
      setAudienceSummary(summary);
      setAudienceTimeseries(ts);
    } finally {
      setAudienceLoading(false);
    }
  }, [keycloak, audienceOpts]);

  useEffect(() => {
    loadAudience();
  }, [loadAudience]);

  const handlePieClick = (entry) => {
    if (entry?.assistant_id) {
      navigate(`/dashboard/usage?assistantId=${entry.assistant_id}`);
    }
  };

  const referrerColumns = [
    { title: 'Referrer', dataIndex: 'domain', key: 'domain', ellipsis: true },
    { title: 'Sessions', dataIndex: 'count', key: 'count', width: 100, align: 'right' },
  ];

  return (
    <>
      <DashboardFilterBar
        filters={[
          { key: 'assistantId', label: 'Assistant' },
        ]}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Requests over time</span>}>
            {loading.requests ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <StackedAreaChart
                height={300}
                data={requestsChart.data}
                emptyState={requestsChart.emptyState}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Token usage over time</span>}>
            {loading.tokenUsage ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <StackedAreaChart
                height={300}
                data={tokenChart.data}
                emptyState={tokenChart.emptyState}
                series={TOKEN_CHART_SERIES}
                valueKeys={TOKEN_TIME_SERIES.valueKeys}
                flatLineKey="tokens"
                totalLabel="Total tokens"
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Assistant usage distribution</span>}>
            {loading.assistantUsage ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : (
              <AssistantsPieChart
                height={380}
                data={assistantUsageData}
                emptyState={pieChartEmptyState}
                legendPosition="bottom"
                onSliceClick={handlePieClick}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Model latency</span>}>
            {loading.modelLatency ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : (
              <ModelLatencyChart
                height={380}
                data={modelLatencyData}
                emptyState={latencyChartEmptyState}
                onBarClick={(modelName) => navigate(`/dashboard/spend?model=${encodeURIComponent(modelName)}`)}
              />
            )}
          </Card>
        </Col>
      </Row>

      <div className="dashboard-audience-section">
        <h3 className="dashboard-section-title">Audience</h3>
        <Text type="secondary" className="dashboard-section-subtitle">
          Widget end-user reach based on sessions and visitor cookies.
        </Text>

        <Row gutter={[16, 16]} style={{ marginTop: 16, marginBottom: 20 }}>
          <Col xs={24} sm={8}>
            <DashboardStatCard title="Unique visitors" loading={audienceLoading}>
              <div className="stat-value">{formatNumber(audienceSummary?.unique_visitors ?? 0)}</div>
              <Text type="secondary" className="dashboard-spend__meta">
                {audienceSummary?.new_visitors ?? 0} new · {audienceSummary?.returning_visitors ?? 0} returning
              </Text>
            </DashboardStatCard>
          </Col>
          <Col xs={24} sm={8}>
            <DashboardStatCard title="Total sessions" loading={audienceLoading}>
              <div className="stat-value">{formatNumber(audienceSummary?.total_sessions ?? 0)}</div>
            </DashboardStatCard>
          </Col>
          <Col xs={24} sm={8}>
            <DashboardStatCard title="Avg messages / session" loading={audienceLoading}>
              <div className="stat-value">
                {audienceSummary?.avg_messages_per_session != null
                  ? audienceSummary.avg_messages_per_session.toFixed(1)
                  : '—'}
              </div>
            </DashboardStatCard>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card className="modern-card" title={<span className="card-title">Sessions & visitors over time</span>}>
              {audienceLoading ? (
                <Skeleton active paragraph={{ rows: 8 }} />
              ) : (
                <DashboardLineChart
                  data={audienceTimeseries}
                  series={AUDIENCE_SERIES}
                  yAxisFormatter={(v) => Math.round(v)}
                  tooltipFormatter={(v) => Math.round(v)}
                  emptyState={<Text type="secondary">No widget sessions in this period.</Text>}
                />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card className="modern-card" title={<span className="card-title">Top referrers</span>}>
              {audienceLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
              ) : (audienceSummary?.top_referrers?.length > 0) ? (
                <Table
                  size="small"
                  rowKey="domain"
                  columns={referrerColumns}
                  dataSource={audienceSummary.top_referrers}
                  pagination={false}
                />
              ) : (
                <Text type="secondary">No referrer data in this period.</Text>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}
